import * as React from 'react'
import { useChartT } from '../i18n'

// Manual-entry checkboxes + cursor-time + noon-shift offset.
type Props = {
  addManualEntryOnDayStart: boolean
  addManualEntryOnDayEnd: boolean
  cursorMinute: number | null
  noonShiftMinutes: number
  onChangeAddManualEntryOnDayStart: (v: boolean) => void
  onChangeAddManualEntryOnDayEnd: (v: boolean) => void
  onChangeNoonShift: (minutes: number) => void
}

export function ChartEditorFooter(p: Props) {
  const T = useChartT()
  // Local draft so the user can freely type partial values (e.g. "1:" before
  // adding the minutes). We commit to the parent only when the input matches
  // HH:MM, and resync the draft whenever the parent value changes (e.g. when
  // the user drags the noon-shift handle on the canvas).
  const [draft, setDraft] = React.useState(minToHHMM(p.noonShiftMinutes))
  React.useEffect(() => {
    setDraft(minToHHMM(p.noonShiftMinutes))
  }, [p.noonShiftMinutes])
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
        <label className="tw-flex tw-items-center tw-gap-2">
          <input
            type="checkbox"
            checked={p.addManualEntryOnDayStart}
            onChange={(e) => p.onChangeAddManualEntryOnDayStart(e.target.checked)}
          />
          {T('manualEntryDayStart', 'Wpis ręczny na początku dnia')}
        </label>
        <label className="tw-flex tw-items-center tw-gap-2">
          <input
            type="checkbox"
            checked={p.addManualEntryOnDayEnd}
            onChange={(e) => p.onChangeAddManualEntryOnDayEnd(e.target.checked)}
          />
          {T('manualEntryDayEnd', 'Wpis ręczny na końcu dnia')}
        </label>
      </div>
      <div className="tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-xs tw-text-slate-500">
          {T('cursorPosition', 'Pozycja kursora:')}
        </span>
        <span className="tw-font-mono tw-text-slate-900">
          {p.cursorMinute == null
            ? '--:--'
            // Cursor minute is the REAL-TIME angular minute (the chart wedges
            // are at real time). For the cursor read-out we show the user's
            // logical/shifted time so the value matches what the rotating
            // hour labels say. 1:1 with WPF
            // TachographChart.cs:598 `_cursorTime + NoonShift.NoonShift`.
            : minToHHMM(p.cursorMinute + p.noonShiftMinutes)}
        </span>
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500">
          {T('timeShift', 'Przesunięcie czasu:')}
          {/* type="text" + pattern instead of type="time" because <input type="time">
              renders as `12:00 AM` in en-US locales; WPF always shows HH:mm 24h. */}
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{1,2}:[0-9]{2}"
            placeholder="00:00"
            value={draft}
            onChange={(e) => {
              const v = e.target.value
              setDraft(v)
              if (/^[0-9]{1,2}:[0-9]{2}$/.test(v)) p.onChangeNoonShift(hhmmToMin(v))
            }}
            onBlur={() => {
              if (!/^[0-9]{1,2}:[0-9]{2}$/.test(draft)) {
                setDraft(minToHHMM(p.noonShiftMinutes))
              }
            }}
            className="tw-w-16 tw-rounded tw-border tw-border-slate-300 tw-px-1 tw-py-0.5 tw-text-center tw-font-mono tw-text-sm tw-text-slate-900"
          />
        </label>
      </div>
    </div>
  )
}

function minToHHMM(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440
  const h = Math.floor(m / 60)
  return `${String(h).padStart(2, '0')}:${String(m - h * 60).padStart(2, '0')}`
}
function hhmmToMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10) || 0)
  return h * 60 + m
}
